import { Alert, MenuItem } from "@mui/material";
import {
  defaultMostSimilarForRole,
  DISREGARD_ROLE,
  PLANT_PROFILE_ROLE_OPTIONS,
  plantProfileRoleI18nKey,
  type PlantProfileRoleOption,
} from "portal/utils/categoryCollection/category";
import { Field } from "formik";
import { PlantProfileRoleIcon } from "../PlantProfileRoleIcon";
import { roleFromJSON } from "protos/robot_syncer/plant_profile";
import { Select, TextField } from "formik-mui";
import { Trans, useTranslation } from "react-i18next";
import React, { FC, SyntheticEvent } from "react";

type CategoryNameAndRoleFieldsProps = {
  role: PlantProfileRoleOption;
  /** When set, role changes keep mostSimilarTo on the role default. */
  mostSimilarTo?: string;
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate?: boolean
  ) => unknown;
  setFieldTouched: (
    field: string,
    touched?: boolean,
    shouldValidate?: boolean
  ) => unknown;
};

const RoleOptionLabel: FC<{ role: PlantProfileRoleOption; label: string }> = ({
  role,
  label,
}) => (
  <span className="flex items-center gap-2">
    <PlantProfileRoleIcon role={roleFromJSON(role)} fontSize="small" />
    {label}
  </span>
);

/**
 * Name, role, and disregard warning shared by create / convert / edit forms.
 * Role select keeps mostSimilarTo on the role default unless the user overrode
 * it. formik-mui Select commits the value in onClose (not onChange).
 */
export const CategoryNameAndRoleFields: FC<CategoryNameAndRoleFieldsProps> = ({
  role,
  mostSimilarTo,
  setFieldValue,
  setFieldTouched,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Field
        component={TextField}
        name="name"
        label={t("components.categoryTemplates.fields.name")}
        fullWidth
      />
      <Field
        component={Select}
        name="role"
        label={t("components.categoryCollectionProfile.fields.role")}
        formControl={{ fullWidth: true }}
        formHelperText={{
          children: t("components.categoryCollectionProfile.fields.roleHelper"),
        }}
        renderValue={(value: unknown) => {
          const selected = PLANT_PROFILE_ROLE_OPTIONS.find(
            (option) => option === value
          );
          if (!selected) {
            return;
          }
          return (
            <RoleOptionLabel
              role={selected}
              label={t(plantProfileRoleI18nKey(selected))}
            />
          );
        }}
        onClose={async ({ target }: SyntheticEvent) => {
          if (!(target instanceof HTMLElement)) {
            setFieldTouched("role", true, true);
            return;
          }
          const nextRole = PLANT_PROFILE_ROLE_OPTIONS.find(
            (option) => option === target.dataset.value
          );
          if (!nextRole) {
            setFieldTouched("role", true, true);
            return;
          }
          await setFieldValue("role", nextRole, false);
          if (
            mostSimilarTo !== undefined &&
            mostSimilarTo === defaultMostSimilarForRole(role)
          ) {
            await setFieldValue(
              "mostSimilarTo",
              defaultMostSimilarForRole(nextRole)
            );
          }
          setFieldTouched("role", true, true);
        }}
      >
        {PLANT_PROFILE_ROLE_OPTIONS.map((option) => (
          <MenuItem key={option} value={option}>
            <RoleOptionLabel
              role={option}
              label={t(plantProfileRoleI18nKey(option))}
            />
          </MenuItem>
        ))}
      </Field>
      {role === DISREGARD_ROLE && (
        <Alert severity="warning">
          <Trans
            i18nKey="components.categoryCollectionProfile.warnings.disregardRole"
            components={[<strong key={0} />]}
          />
        </Alert>
      )}
    </>
  );
};
